/** Global access_config key for value-level rules on a custom input (leads & profiles). */
export function customFieldModuleKey(customInputId: number | string): string {
  return `custom_field_${customInputId}`;
}

/**
 * What the caller needs from `usePermissions()` to answer a per-field question.
 * A bare `canGlobal` is still accepted for older call sites, but WITHOUT
 * `getRawGlobalModule` the helper cannot tell "no rule stored" from "denied",
 * so it falls back to the pre-redesign behaviour (only `isNeedPermission`
 * fields are gated).
 */
export type CustomFieldPerms =
  | ((moduleKey: string, action: string) => boolean)
  | {
      canGlobal: (moduleKey: string, action: string) => boolean;
      getRawGlobalModule?: (moduleKey: string) => any;
    };

function normalise(perms: CustomFieldPerms) {
  return typeof perms === "function"
    ? { canGlobal: perms, getRawGlobalModule: undefined }
    : perms;
}

/** Does the role carry an explicit rule for this field? */
function hasStoredRule(
  fieldId: number | string,
  getRawGlobalModule?: (moduleKey: string) => any
): boolean {
  if (!getRawGlobalModule) return false;
  const mod = getRawGlobalModule(customFieldModuleKey(fieldId));
  if (!mod || typeof mod !== "object") return false;
  return (
    typeof mod.level === "string" ||
    typeof mod.view === "string" ||
    typeof mod.edit === "string" ||
    typeof mod.access === "string"
  );
}

/**
 * Per-field, per-action gate.
 *
 * THE RULE, in order:
 *   1. An explicit `global.custom_field_<id>` rule always wins — for ANY
 *      field. The Edit Role wizard now lists every custom field, so Deny /
 *      View / Edit / Manage is expressible on all of them, not just the ones
 *      flagged "use for access control".
 *   2. No rule + `isNeedPermission` → defer to `canGlobal` (a gated field with
 *      nothing stored stays closed, as it always has).
 *   3. No rule + role-neutral field → allowed. Load-bearing: roles saved
 *      before the wizard listed every field are silent about most of them, and
 *      reading that silence as Deny would blank every custom field in the
 *      workspace the day this shipped.
 *
 * The SERVER is the real enforcement (`scope/customFieldScope/customFieldFieldAccess.ts`
 * strips denied fields out of `/custom` and `/specifics`, off record payloads,
 * off exports and out of `cf_*` filters). This helper keeps the UI honest for
 * anything already in the client's hands.
 */
export function canAccessCustomField(
  field: { id: number | string; isNeedPermission?: boolean } | undefined | null,
  action: "view" | "edit",
  perms: CustomFieldPerms
): boolean {
  if (!field) return false;
  const { canGlobal, getRawGlobalModule } = normalise(perms);
  if (hasStoredRule(field.id, getRawGlobalModule)) {
    return canGlobal(customFieldModuleKey(field.id), action);
  }
  if (field.isNeedPermission !== true) return true;
  return canGlobal(customFieldModuleKey(field.id), action);
}

/**
 * Filter custom input fields by per-role view permission. Same rule ladder as
 * {@link canAccessCustomField} — an explicitly denied field is dropped whether
 * or not it opted into access control.
 */
export function filterCustomInputFieldsByViewPermission<
  T extends { id: number | string; isNeedPermission?: boolean },
>(fields: T[] | undefined, perms: CustomFieldPerms): T[] {
  if (!fields?.length) return [];
  return fields.filter((f) => canAccessCustomField(f, "view", perms));
}
